import { css, styled } from 'styled-components';

import { colors, textStyles as styles } from '#/theme/theme';

// the RTE would generate HTML like <u><a>...</a></u> which would show
// up with a black underline instead of blue. override this here
// (#188984192)

const underlineFix = css`
  u {
    &:has(a) {
      text-decoration-color: ${colors.brandBlue};
    }
    &:has(a):hover {
      text-decoration-color: ${colors.hoverBlue};
    }
  }
`;

export const StyledRichText = styled.div<{
  $simple?: boolean;
  $small?: boolean;
  $normalize?: boolean;
  $textAlignCenter?: boolean;
  $noMargin?: boolean;
  $color?: string;
}>`
  font-weight: 400;
  font-size: 18px;
  line-height: 1.75;

  &:first-child {
    margin-top: 0;
  }

  & > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    font-size: 20px;
  }

  * {
    margin-bottom: 0;
  }

  *:first-child {
    margin-top: 0;
  }

  h1 {
    font-weight: 600;
    font-size: 30px;
    line-height: 1.125;
  }

  h1:first-child {
    margin-top: 0;
  }

  h1 > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    h1 {
      font-size: 40px;
    }
  }

  @media all and (min-width: 992px) {
    h1 {
      font-size: 48px;
    }
  }

  h2 {
    font-weight: 600;
    font-size: 26px;
    line-height: 1.25;
  }

  h2:first-child {
    margin-top: 0;
  }

  h2 > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    h2 {
      font-size: 32px;
    }
  }

  @media all and (min-width: 992px) {
    h2 {
      font-size: 36px;
    }
  }

  h3 {
    font-weight: 600;
    font-size: 22px;
    line-height: 1.4583;
  }

  h3:first-child {
    margin-top: 0;
  }

  h3 > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    h3 {
      font-size: 24px;
    }
  }

  h4 {
    font-weight: 600;
    font-size: 18px;
    line-height: 1.75;
  }

  h4:first-child {
    margin-top: 0;
  }

  h4 > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    h4 {
      font-size: 20px;
    }
  }

  h5 {
    font-weight: 600;
    font-size: 14px;
    line-height: 1.875;
  }

  h5:first-child {
    margin-top: 0;
  }

  h5 > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    h5 {
      font-size: 16px;
    }
  }

  h1,
  h2,
  h3 {
    margin-top: 40px;
  }

  h1 + *,
  h2 + *,
  h3 + * {
    margin-top: 32px;
  }

  h1 strong,
  h2 strong,
  h3 strong,
  h1 u,
  h2 u,
  h3 u,
  h1 b,
  h2 b,
  h3 b,
  h1 em,
  h2 em,
  h3 em {
    background-image: linear-gradient(
      to bottom,
      var(--color-brandYellow) 0%,
      var(--color-brandYellow) 100%
    );
    background-position: bottom;
    background-repeat: repeat-x;
    background-size: 100% 5px;
    text-decoration: none;
    font-weight: 600;
    font-style: normal;
  }

  h4,
  h5,
  p,
  ul,
  ol {
    margin: 0.75em 0;
  }

  h4 + p,
  h5 + p,
  p + p,
  ul + p,
  ol + p,
  h4 + ul,
  h5 + ul,
  p + ul,
  ul + ul,
  ol + ul,
  h4 + ol,
  h5 + ol,
  p + ol,
  ul + ol,
  ol + ol,
  h4 + h4,
  h5 + h4,
  p + h4,
  ul + h4,
  ol + h4,
  h4 + h5,
  h5 + h5,
  p + h5,
  ul + h5,
  ol + h5 {
    margin-top: 0.75em;
  }

  p,
  ul,
  ol {
    font-weight: 400;
    line-height: 1.75;
    font-size: inherit;
  }

  p:first-child,
  ul:first-child,
  ol:first-child {
    margin-top: 0;
  }

  p > *:first-child,
  ul > *:first-child,
  ol > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    p,
    ul,
    ol {
      font-size: 20px;
    }
  }

  @media all and (min-width: 768px) {
    p,
    ul,
    ol {
      font-size: inherit;
    }
  }

  ul,
  ol {
    list-style-type: none;
    margin-left: 0;
    padding-left: 0;
  }

  ul li,
  ol li {
    position: relative;
    padding-left: 48px;
  }

  ul li:not(.ql-indent-1, .ql-indent-2, .ql-indent-3),
  ol li:not(.ql-indent-1, .ql-indent-2, .ql-indent-3) {
    counter-reset: list-1 list-2 list-3;
  }

  ul li.ql-indent-1,
  ol li.ql-indent-1 {
    counter-reset: list-2 list-3;
    padding-left: 96px;
  }

  ul li.ql-indent-2,
  ol li.ql-indent-2 {
    counter-reset: list-3;
    padding-left: 144px;
  }

  ul li::before,
  ol li::before {
    position: absolute;
    left: 0;
    top: 0;
    color: var(--color-brandBlue);
    display: inline-block;
    width: 36px;
    text-align: right;
  }

  ul li.ql-indent-1::before,
  ol li.ql-indent-1::before {
    left: 48px;
  }

  ul li.ql-indent-2::before,
  ol li.ql-indent-2::before {
    left: 96px;
  }

  ul li.ql-indent-3,
  ol li.ql-indent-3 {
    padding-left: 192px;
  }

  ul li.ql-indent-3::before,
  ol li.ql-indent-3::before {
    left: 144px;
  }

  ul li::before {
    content: '•';
  }

  ol {
    counter-reset: ordered-list;
  }

  ol > li {
    counter-increment: ordered-list;
  }

  ol > li::before {
    content: counter(ordered-list) '.';
  }

  ol > li.ql-indent-1 {
    counter-increment: list-1;
  }

  ol > li.ql-indent-1::before {
    content: counter(list-1) '.';
  }

  ol > li.ql-indent-2 {
    counter-increment: list-2;
  }

  ol > li.ql-indent-2::before {
    content: counter(list-2) '.';
  }

  ol > li.ql-indent-3 {
    counter-increment: list-3;
  }

  ol > li.ql-indent-3::before {
    content: counter(list-3) '.';
  }

  a {
    overflow-wrap: break-word;
  }

  a,
  a:active,
  a:visited {
    text-decoration: none;
    color: var(--color-brandBlue);
  }

  a:hover {
    color: var(--color-hoverBlue);
    text-decoration: underline;
    text-decoration-color: var(--color-hoverBlue);
  }

  strong {
    font-weight: 600;
  }

  blockquote {
    position: relative;
    width: calc(100% + 48px);
    margin: 32px -24px;
    padding: 32px 24px;
    font-weight: 400;
    font-size: 24px;
    line-height: 1.5;
    text-align: center;
    border-radius: 10px;
    background: var(--color-backgroundGrey);
  }

  blockquote:first-child {
    margin-top: 0;
  }

  blockquote > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    blockquote {
      font-size: 30px;
    }
  }

  blockquote::before {
    content: '“';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -35%);
    color: var(--color-brandYellow);
    font-size: 100px;
    font-weight: 700;
    line-height: 1;
  }

  blockquote[cite]::after {
    content: attr(cite);
    margin-top: 24px;
    display: block;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.875;
  }

  blockquote[cite]::after:first-child {
    margin-top: 0;
  }

  blockquote[cite]::after > *:first-child {
    margin-top: 0;
  }

  @media all and (min-width: 768px) {
    blockquote[cite]::after {
      font-size: 16px;
    }
  }

  blockquote[cite]::after a {
    font-weight: 600;
  }

  @media all and (min-width: 768px) {
    blockquote {
      width: calc(100% + 96px);
      margin: 64px -48px;
      padding: 60px 48px;
    }
  }

  .idlst-rchtxt:first-child {
    margin-top: 0;
  }

  .idlst-rchtxt:first-child > *:first-child {
    margin-top: 0;
  }

  .idlst-rchtxt:last-child {
    margin-bottom: 0;
  }

  .idlst-rchtxt:last-child > *:nth-child(n + 2):last-child {
    margin-bottom: 0;
  }

  .ql-align-center {
    text-align: center;
  }

  .ql-align-right {
    text-align: right;
  }

  overflow-wrap: break-word;

  ${underlineFix}

  & {
    /* block-no-empty */

    ${(props) =>
      props.$simple &&
      `
    h1, h2, h3, h4, h5 {
      margin: 1em 0;
      font-size: inherit !important;
    }
  `}
    ${(props) =>
      props.$small &&
      `
    ${styles.bodySmall}

    h1, h2, h3, h4 {
      margin: 1em 0;
      ${styles.h4}
    }
  `}

  ${(props) =>
      props.$normalize &&
      `
    h1, h2, h3 {
      ${styles.h3}
    }

    h4, h5 {
      font-size: inherit !important;
    }

    h1, h2, h3, h4, h5 {
      margin: 1em 0;
    }
  `}
  }

  ${(props) => props.$textAlignCenter && 'text-align: center;'};
  ${(props) => props.$noMargin && 'margin: 0;'};
  ${(props) => props.$color && `color: ${props.$color};`};
`;
